<template>
	<div :class="mode === 'guestPlaylist' ? 'col-sm-12 col-xl-6' : 'col-sm-6 col-xl-4'" class="draggable col-xs-12 col-md-6 relative-position">
		<AddNewPlaylist :show-new-playlist-form="showNewPlaylistForm" :project-id="projectID" :user-uuid="userStatus.uuid" @close="showNewPlaylistForm = false" />
		<q-card>
			<div v-if="mode === 'list' || mode === 'playlistEdit' || mode === 'playlistView' || mode === 'guestPlaylist'" class="ribbon ribbon-big">
				<span v-if="project.usage_rights_status?.cockpit_label.toLowerCase() == 'nda'" class="nda">nda</span>
				<span v-if="project.usage_rights_status?.cockpit_label.toLowerCase() == 'share with customer' && mode !== 'guestPlaylist'" class="share"> share </span>
				<span v-if="project.usage_rights_status?.cockpit_label.toLowerCase() == 'show sequence in meetings' && mode !== 'guestPlaylist'" class="show"> show </span>
			</div>
			<div v-if="getFormat(project, false).indexOf('video maker') > -1" class="ribbon ribbon-big-left">
				<span class="video-maker">video maker</span>
			</div>
			<div :class="[playButtonsSorted(project.languages)?.topTwo?.length > 0 ? 'cursor-pointer' : '']" @click="autoPlayFirstLanguage(project)">
				<q-card-media>
					<img v-if="project.poster" :src="posterURL(project)" @error="swapPlaceholderImage" />
					<img v-else src="/images/cockpit/projects/thumbs/example-video.svg" />
				</q-card-media>
			</div>
			<q-card-separator />

			<q-card-title class="card">
				<!-- =====================================
						=                PLAY BUTTONS                =
						====================================== -->
				<LanguageButtons :project="project" :play-buttons-sorted="playButtonsSorted(project.languages)"></LanguageButtons>

				<div class="the-title">
					{{ cardTitle.text }}<span class="text-italic">{{ cardTitle.details }}</span>
					<q-tooltip :delay="1000" anchor="bottom middle" self="top middle">{{ cardTitle.tooltip }}</q-tooltip>
				</div>
				<template #subtitle>
					{{ cardSubtitle.text }}<span class="text-italic">{{ cardSubtitle.details }}</span>
					<q-tooltip :delay="1000" anchor="bottom middle" self="top middle">{{ cardSubtitle.tooltip }}</q-tooltip>
				</template>
				<template #right>
					<div class="row items-center">
						<div class="extra-info">
							<div v-if="project.entity">
								{{ project.entity.title }}<br />
								{{ $moment(project.release_date).format('YYYY') }}
							</div>
						</div>
					</div>
				</template>
			</q-card-title>

			<!-- MORE -->
			<q-card-main v-if="mode === 'guestPlaylist'">
				<div class="logo">
					<img :src="'/images/cockpit/logos/simpleshow_' + getFormat(project, true) + '.svg'" alt="Playlist" />
				</div>
			</q-card-main>
			<q-btn-dropdown v-if="mode !== 'guestPlaylist'" no-ripple label="more..." style="width: 100%" :class="['card-dropdown-btn', 'card-dropdown-btn-' + getFormat(project, true)]">
				<q-list link label="more...">
					<div>
						<q-card-main>
							<div class="main_info q-mb-lg">
								<div>
									<span class="text-faded">original title:</span>
									<span>{{ project.title_original }}</span>
								</div>
								<div>
									<span class="text-faded">product group:</span>
									<span>{{ project?.type?.title }}</span>
								</div>
								<div v-if="project?.type?.title === 'interactive'">
									<span class="text-faded">interactive format:</span>
									<span>{{ project?.interactive_format?.title }}</span>
								</div>
								<div>
									<span class="text-faded">format:</span>
									<span>{{ project?.format?.title }}</span>
								</div>
								<div>
									<span class="text-faded">format international:</span>
									<span>{{ project?.format_int?.title }}</span>
								</div>
								<div>
									<span class="text-faded">style:</span>
									<span>{{ project?.style?.title }}</span>
								</div>
								<div>
									<span class="text-faded">origin:</span>
									<span>{{ project?.origin?.title }}</span>
								</div>
								<div>
									<span class="text-faded">music:</span>
									<span>{{ project?.music?.title }}</span>
								</div>
								<div v-if="project?.online_resource?.url">
									<span class="text-faded">link:</span>
									<span
										><a :href="project?.online_resource?.url" target="_blank" rel="noopener noreferrer" style="color: #f90; text-decoration: underline">{{ project.online_resource.description || project?.online_resource?.url }}</a></span
									>
								</div>
							</div>
							<div class="meta_info q-mb-lg">
								<span class="text-faded">project number:</span>
								P-{{ project.project_number }}
								<br />
								<span class="text-faded">opportunity number:</span>
								{{ project.opportunity_number }}
								<br />
								<span class="text-faded">id:</span>
								{{ project.id }}
								<span class="text-faded"> / hash id:</span>
								{{ project.hash_id }}
								<br />
								<div v-if="userCan('view project editor')">
									<span class="text-faded">created: </span>
									{{ project.created_at }}
									<span v-if="project.user">
										<span class="text-faded">by </span>
										{{ project.user?.full_name }}
									</span>
								</div>
							</div>

							<!-- CHIPS -->
							<div v-if="project.department?.title" class="industry text-left q-mb-md">
								<div class="text-faded">department:</div>
								<q-chip small color="simpleshow" class="q-mr-sm q-my-xs">{{ project.department?.title }}</q-chip>
							</div>

							<div v-if="project.customer?.industry" class="industry text-left q-mb-md">
								<div class="text-faded">customer industries:</div>
								<q-chip small color="simpleshow" class="q-mr-sm q-my-xs">{{ project.customer?.industry.title }}</q-chip>
							</div>

							<div v-if="project.customer?.corporate_group?.id" class="industry text-left q-mb-md">
								<div class="text-faded">corporate group:</div>
								<q-chip small color="simpleshow" class="q-mr-sm q-my-xs">{{ project.customer?.corporate_group?.name }}</q-chip>
							</div>

							<div v-if="project.customer?.corporate_group?.industry?.title" class="industry text-left q-mb-md">
								<div class="text-faded">corporate group industries:</div>
								<q-chip small color="simpleshow" class="q-mr-sm q-my-xs">{{ project.customer?.corporate_group?.industry?.title }}</q-chip>
							</div>

							<div v-if="project.department?.length" class="departments text-left q-mb-md mb-3 text-faded">
								<div class="text-faded">company department:</div>
								<q-chip small color="simpleshow" class="q-mr-sm q-mb-sm">{{ project.department?.title }}</q-chip>
							</div>

							<div v-if="project.topics?.length" class="topics text-left q-mb-md mb-3 text-faded">
								<div class="text-faded">topics:</div>
								<q-chip v-for="topic in project.topics" :key="topic.id" small color="simpleshow" class="q-mr-sm q-my-xs">{{ topic.title }}</q-chip>
							</div>

							<div v-if="project.video_features?.length" class="video_features text-left q-mb-md mb-3 text-faded">
								<div class="text-faded">video features:</div>
								<q-chip v-for="video_feature in project.video_features" :key="video_feature.id" small color="simpleshow" class="q-mr-sm q-my-xs">{{ video_feature.title }}</q-chip>
							</div>

							<div v-if="project.interactive_features?.length" class="interactive_features text-left q-mb-md mb-3 text-faded">
								<div class="text-faded">interactive features:</div>
								<q-chip v-for="interactive_feature in project.interactive_features" :key="interactive_feature.id" small color="simpleshow" class="q-mr-sm q-my-xs">{{ interactive_feature.title }}</q-chip>
							</div>

							<div v-if="project.usage_rights_status?.length" class="interactive_features text-left q-mb-md mb-3 text-faded">
								<div class="text-faded">usage rights:</div>
								<q-chip v-if="project.usage_rights_status.cockpit_label" small color="simpleshow" class="q-mr-sm q-my-xs">{{ project.usage_rights_status.cockpit_label }}</q-chip>
							</div>
						</q-card-main>
					</div>
				</q-list>
			</q-btn-dropdown>

			<q-card-separator />

			<q-card-actions v-if="(mode === 'list' || (userIsLoggedIn && mode === 'guestPlaylist')) && !isSandbox" align="around">
				<q-btn-dropdown label="playlist" size="md" flat icon="add" class="no-focus">
					<q-list link>
						<q-item v-close-overlay @click.native="showAddNewPlaylistModal(project.id)">
							<q-item-side icon="assignment" />
							<q-item-main>
								<q-item-tile label>Create a new playlist</q-item-tile>
							</q-item-main>
						</q-item>
						<q-item-separator />
						<q-item v-for="playlist in playlists" :key="playlist.id" v-close-overlay @click.native="addToOrRemoveFromPlaylist('simpleshow project', playlist, project)">
							<q-item-side v-if="projectInPlaylist(project.id, playlist)" icon="favorite" color="red" />
							<q-item-side v-else icon="add" />
							<q-item-main>
								<q-item-tile label>{{ playlist.title }}</q-item-tile>
							</q-item-main>
						</q-item>
					</q-list>
				</q-btn-dropdown>
			</q-card-actions>
		</q-card>

		<div v-if="mode === 'list'" style="text-align: center">
			<q-btn v-if="userCan('edit project') && project.hash_id" :to="{ name: 'projectEdit', params: { id: project.hash_id } }" color="simpleshow" size="md" label="Edit" icon="create" dense style="width: 32%" />
			<q-btn v-if="userCan('delete project')" color="simpleshow" size="md" label="Delete" icon="delete" style="width: 32%" dense @click="showDeleteAlert('project', project.title, project.hash_id)" />
			<q-btn v-if="!isSandbox && userCan('hide project')" color="simpleshow" size="md" label="Hide" icon="visibility_off" style="width: 32%" dense @click="hide(project.hash_id)" />
			<q-btn v-if="isSandbox && userCan('publish project')" color="simpleshow" size="md" label="Publish" icon="visibility" style="width: 32%" dense @click="unhide(project.hash_id)" />
		</div>

		<div v-if="mode === 'playlistEdit'" style="text-align: center">
			<q-btn color="simpleshow" size="md" label="Remove" icon="delete" style="width: 32%" @click="removeFromPlaylist(project)" />
		</div>
	</div>
</template>

<script>
	import { firstBy } from 'thenby';

	import AddNewPlaylist from '../AddNewPlaylist.vue';
	import LanguageButtons from '../LanguageButtons.vue';

	export default {
		name: 'ProjectCard',
		components: { LanguageButtons, AddNewPlaylist },
		props: {
			projectProp: {
				type: Object,
				default() {
					return {};
				}
			},

			userStatus: {
				type: Object,
				default() {
					return {};
				}
			},

			mode: {
				type: String,
				default: '',
				validator: value => {
					return ['list', 'playlistEdit', 'playlistView', 'guestPlaylist', 'salesDemo'].includes(value);
				}
			},

			isSandbox: {
				type: Boolean,
				default: false
			}
		},

		data() {
			return {
				showNewPlaylistForm: false,
				userIsLoggedIn: window.userIsLoggedIn,
				projectID: '',
				project: this.projectProp
			};
		},

		computed: {
			playlists() {
				return this.$store.getters.getPlaylists;
			},

			cardTitle() {
				var project = this.project.title;
				var customer = this.project.customer?.name;
				var details = '';
				if (this.project.customer?.corporate_group?.name) {
					details = ' (' + this.project.customer?.corporate_group?.name + ')';
				}
				if (this.mode === 'guestPlaylist') {
					return {
						text: customer,
						details: '',
						tooltip: customer + details
					};
				} else {
					return {
						text: project,
						details: '',
						tooltip: project
					};
				}
			},

			targetRegion() {
				if (this.mode === 'guestPlaylist') {
					return window?.playlist?.user?.entity?.title?.toLowerCase() || this?.userStatus?.entity_title?.toLowerCase();
				} else {
					return this?.userStatus?.entity_title?.toLowerCase();
				}
			},

			cardSubtitle() {
				var project = this.project.title;
				var customer = this.project.customer?.name;
				var details = '';
				if (this.project.customer?.corporate_group?.name) {
					details = ' (' + this.project.customer?.corporate_group?.name + ')';
				}
				if (this.mode === 'guestPlaylist') {
					return {
						text: project,
						details: '',
						tooltip: project
					};
				} else {
					return {
						text: customer,
						details: details,
						tooltip: customer + details
					};
				}
			}
		},

		created() {},

		methods: {
			autoPlayFirstLanguage(project) {
				if (this.playButtonsSorted(project.languages)?.topTwo?.length === 0) {
					return;
				}

				const langDescription = this.playButtonsSorted(project.languages)?.topTwo?.[0]?.description;
				const langCode = this.playButtonsSorted(project.languages)?.topTwo?.[0]?.code;

				this.setupVideoPlayerModal(project, langDescription, langCode);
			},

			swapPlaceholderImage(event) {
				event.target.src = '/images/cockpit/projects/thumbs/example-video.svg';
			},

			removeFromPlaylist(project) {
				this.$store.dispatch('removeVirtualProjectFromPlaylist', project.hash_id);
				this.$ToastSwal.fire('Info:', 'Submit playlist for the changes to take effect.', 'warning');
			},

			projectInPlaylist(projectID, playlist) {
				return playlist?.project_ids?.includes(projectID);
			},

			setupVideoPlayerModal(project, language, languageCode) {
				if (project?.type?.title === 'interactive') {
					this.$store.dispatch('setInteractiveModal', {
						project: project,
						language: language,
						languageCode: languageCode,
						posterMode: false
					});
				} else {
					this.$store.dispatch('setVideoPlayerModal', {
						project: project,
						language: language,
						languageCode: languageCode,
						posterMode: false
					});
				}
			},

			showAddNewPlaylistModal(id) {
				this.projectID = id;
				this.showNewPlaylistForm = true;
			},

			addToOrRemoveFromPlaylist(object, playlist, project) {
				if (this.projectInPlaylist(project.id, playlist)) {
					console.log('Already in.');
					this.$store.dispatch('editPlaylist', {
						hash_id: playlist.hash_id,
						title: playlist.title,
						description: playlist.description,
						projects: [project],
						user_uuid: this.userStatus.uuid,
						type: 'removeProjectFromPlaylist'
					});
				} else {
					console.log('Not there yet');
					this.$store.dispatch('editPlaylist', {
						hash_id: playlist.hash_id,
						title: playlist.title,
						description: playlist.description,
						projects: [project],
						user_uuid: this.userStatus.uuid,
						type: 'addProjectToPlaylist'
					});
				}
			},

			showDeleteAlert(object, title, id) {
				this.$DeleteSwal
					.fire({
						title: 'Are you sure you want to<br>delete the ' + object + '?',
						text: title
					})
					.then(result => {
						if (result.value) {
							this.delete(id);
							this.$ToastSwal.fire('Deleted!', 'The ' + object + ' has been deleted.', 'success');
						} else {
							this.$ToastSwal.fire('Cancelled!', 'The ' + object + ' is safe.', 'error');
						}
					});
			},

			delete(id) {
				console.log('Delete project with ID: #' + id);
				this.$store.dispatch('projects/removeVirtualProject', id);
				this.$store.dispatch('projects/deleteSingle', id);
			},

			hide(id) {
				console.log('Hiding project with ID: #' + id);
				this.$store.dispatch('projects/moveVirtualProject', id);
				this.$store.dispatch('projects/hideSingle', id);
			},

			unhide(id) {
				console.log('Un-hiding project with ID: #' + id);
				this.$store.dispatch('projects/moveVirtualProject', id);
				this.$store.dispatch('projects/unhideSingle', id);
			},

			posterURL(project) {
				const poster = project.uploads.find(upload => upload.type === 'poster');
				const posterDate = poster ? poster.updated_at : null;
				const posterDateTime = posterDate ? new Date(posterDate).getTime() : null;

				return `/storage/projects/data/${project.hash_id}/poster.jpg?${posterDateTime}`;
			},

			userCan(permission) {
				return this.$store.getters.userCan(permission);
			},

			isDACH(entityTitle) {
				const dachArray = ['germany', 'austria', 'switzerland', 'united kingdom'];
				if (dachArray.includes(entityTitle.toLowerCase())) {
					return true;
				} else {
					return false;
				}
			},

			getFormat(project, websafe) {
				let formatTitle = '';

				if (project.type?.title.toLowerCase() === 'explainer video') {
					if (this.isDACH(this.targetRegion)) {
						formatTitle = project?.format?.title;
					} else {
						formatTitle = project?.format_int?.title;
					}
				} else if (project.type?.title.toLowerCase() === 'interactive') {
					formatTitle = 'interactive';
				}

				if (websafe) {
					formatTitle = formatTitle.replaceAll(' ', '-').replaceAll('-(old)', '');
				}

				return formatTitle;
			},

			playButtonsSorted(languagesArray) {
				if (!languagesArray) return null;
				var tempArray = JSON.parse(JSON.stringify(languagesArray));
				var sorted = tempArray.sort(firstBy('code'));

				var uploadedLanguages = sorted.filter(function (el) {
					return el.uploaded === 1;
				});

				/**
				 * @param {Array} array If only I knew
				 * @param {string} code The language code I guess?
				 * @returns {number} Something something sorting.
				 */
				function findLanguage(array, code) {
					var tempIndex = '';
					if (array.findIndex(x => x.code === code) > -1) {
						tempIndex = array.findIndex(x => x.code === code);
						return tempIndex;
					} else {
						code = code.substring(0, 3);
						if (array.findIndex(x => x.code.startsWith(code)) > -1) {
							tempIndex = array.findIndex(x => x.code.startsWith(code));
							return tempIndex;
						}
					}
					return -1;
				}

				var topTwo = [];
				var tempIndex1 = findLanguage(uploadedLanguages, 'de_DE');
				var tempIndex2 = findLanguage(uploadedLanguages, 'en_US');
				if (tempIndex1 > -1) {
					topTwo.push(uploadedLanguages[tempIndex1]);
				}
				if (tempIndex2 > -1) {
					topTwo.push(uploadedLanguages[tempIndex2]);
				}
				var uploadedLanguagesFiltered = uploadedLanguages.filter(val => !val.code.startsWith('de_') && !val.code.startsWith('en_'));

				while (topTwo.length < 2 && uploadedLanguagesFiltered.length > 0) {
					topTwo.push(uploadedLanguagesFiltered.shift());
				}
				uploadedLanguages = uploadedLanguages.filter(val => !topTwo.includes(val));
				while (topTwo.length < 2 && uploadedLanguages.length > 0) {
					topTwo.push(uploadedLanguages.shift());
				}

				if (uploadedLanguages.length === 1) {
					topTwo.push(uploadedLanguages.shift());
				}

				var tempArray1 = [];
				var tempArray2 = [];

				uploadedLanguages.forEach(function (element) {
					tempArray1.push(element.code);
				});
				topTwo.forEach(function (element) {
					tempArray2.push(element.code);
				});

				return { topTwo: topTwo, rest: uploadedLanguages };
			}
		}
	};
</script>

<style lang="scss" scoped>
	/* ########## ProjectCard.vue ########## */
	button.card-dropdown-btn {
		flex-direction: column;
		box-shadow: none;
	}

	body.guest-playlist {
		.the-title {
			font-size: 18px;
			font-weight: 700;
		}
	}
</style>
